import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { RxTemplate } from '@shared/rx-templates/rx-template.model';
import { ProcedureMap } from '@shared/models/procedure-map';
import { OrderQuery } from '@modules/order/state/order-query';
import { DentureBaseFieldsVisibility, DentureDetailsVisibilityService } from '@modules/denture-details/denture-details-visibility.service';
import { DentureDetailsUtils } from '@modules/denture-details/utils/denture-details-utils';
import { IDentureVisibilityService } from '@modules/denture-details/interfaces/denture-visibility';

/** Field-level visibility flags for the intuitive denture section (includes procedureType). */
export interface RxIntuitiveDentureFieldsVisibility extends DentureBaseFieldsVisibility {
	/** Procedure type dropdown — hidden via template.ui.denture.items.procedureType.hidden. */
	procedureType: boolean;
}

@Injectable({ providedIn: 'root' })
export class RxIntuitiveDentureVisibilityService implements IDentureVisibilityService {
	readonly fields$: Observable<RxIntuitiveDentureFieldsVisibility> = this.rxTemplateService.template$.pipe(
		map(template => RxIntuitiveDentureVisibilityService.fieldsFromTemplate(template)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	private readonly isSectionNonEmpty$: Observable<boolean> = this.fields$.pipe(
		map(f => RxIntuitiveDentureVisibilityService.isSectionNonEmpty(f))
	);

	readonly isSectionVisible$: Observable<boolean> = combineLatest([this.orderQuery.procedureMap$, this.isSectionNonEmpty$]).pipe(
		map(([procedureMap, nonEmpty]) => DentureDetailsUtils.isDentureDetailsEnabled(procedureMap?.ProcedureId) && nonEmpty),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	static fieldsFromTemplate(template: Readonly<RxTemplate>): RxIntuitiveDentureFieldsVisibility {
		return {
			...DentureDetailsVisibilityService.fieldsFromTemplate(template),
			procedureType: !template.ui.denture?.items.procedureType?.hidden
		};
	}

	static isSectionNonEmpty(f: RxIntuitiveDentureFieldsVisibility): boolean {
		return f.procedureType || DentureDetailsVisibilityService.isSectionNonEmpty(f);
	}

	constructor(
		private readonly rxTemplateService: RxTemplateService,
		private readonly orderQuery: OrderQuery
	) {}

	fieldsFor$(
		targetTemplate: Readonly<RxTemplate>,
		targetProcedureMap: ProcedureMap | null
	): Observable<RxIntuitiveDentureFieldsVisibility> {
		// If the target procedure map is not a denture procedure, all fields will be hidden.
		if (!DentureDetailsUtils.isDentureDetailsEnabled(targetProcedureMap?.ProcedureId)) {
			return of({ procedureType: false, stage: false, mould: false, shadeSystem: false, teethShade: false, gingival: false });
		}

		return of(RxIntuitiveDentureVisibilityService.fieldsFromTemplate(targetTemplate));
	}
}
